import { Dispatch, SetStateAction } from 'react';

import { ToastConfig } from '@/components/Toast';

import { EMessageRole, TArtifact, TMessage } from './types';

export const updateMessageState = (
  setMessages: Dispatch<SetStateAction<TMessage[]>>,
  content: string,
  isFirstFollowup: boolean
) => {
  if (isFirstFollowup) {
    setMessages((prev) => {
      return [
        ...prev,
        {
          type: EMessageRole.AI,
          content,
        },
      ];
    });
  } else {
    setMessages((prev) => {
      const lastMessageContent = prev[prev.length - 1].content;
      return [
        ...prev.slice(0, -1),
        {
          type: EMessageRole.AI,
          content: lastMessageContent + content,
        },
      ];
    });
  }
};

export const updateArtifactContent = (
  setArtifact: Dispatch<SetStateAction<TArtifact>>,
  content: string,
  newArtifactIndex: number,
  overwriteContent: boolean = false
) => {
  setArtifact((prev) => {
    const prevArtifact = prev.contents.find(
      (c) => c.index === newArtifactIndex
    );

    if (prevArtifact) {
      // New artifact content exists in state, update it
      const updatedContent = overwriteContent
        ? content
        : prevArtifact.content + content;
      return {
        id: prev.id,
        current_content_index: newArtifactIndex,
        contents: [
          ...prev.contents.filter((c) => c.index !== newArtifactIndex),
          {
            index: newArtifactIndex,
            content: updatedContent,
          },
        ],
      };
    }

    // New artifact content has not been added in state yet, create it
    return {
      id: prev.id,
      contents: [
        ...prev.contents,
        {
          index: newArtifactIndex,
          content,
        },
      ],
      current_content_index: newArtifactIndex,
    };
  });
};

export const getStreamEventData = (
  data: any
): Record<string, any> | undefined => {
  try {
    const chunk = data();
    if (chunk?.value) {
      const { patch } = JSON.parse(chunk.value);
      const eventData: Record<string, any>[] | undefined = patch || undefined;
      if (!eventData || eventData.length === 0) {
        return undefined;
      }
      return eventData[0];
    }
  } catch (e) {
    // no-op
  }
};

/**
 * Updates text by gradually replacing selected portion with streamed chunks
 * @param originalText The complete text
 * @param selectionStart Starting index of selected text
 * @param selectionEnd Ending index of selected text
 * @param newChunk New chunk of text to insert (or complete replacement if isStreamComplete is true)
 * @param previouslyProcessed Number of characters already processed from previous chunks
 * @param isStreamComplete Whether this is the final message
 * @param createToast Function to create a toast notification
 * @returns The number of characters processed
 */
export function updateStreamedText(fields: {
  setArtifact: Dispatch<SetStateAction<TArtifact>>;
  newArtifactIndex: number;
  viewingArtifactContent: string;
  selectionStart: number;
  selectionEnd: number;
  newChunk: string;
  previouslyProcessed: number;
  isStreamComplete?: boolean;
  createToast: (titleOrConfig: ToastConfig | string) => void;
}): number {
  const {
    setArtifact,
    newArtifactIndex,
    viewingArtifactContent,
    selectionStart,
    selectionEnd,
    newChunk,
    previouslyProcessed,
    isStreamComplete,
    createToast,
  } = {
    isStreamComplete: false,
    ...fields,
  };
  // Input validation
  if (typeof newChunk !== 'string') {
    createToast({
      title: 'Error updating prompt.',
      description: 'Invalid text received.',
      error: true,
    });
    return previouslyProcessed;
  }

  // Handle stream completion - use newChunk as the complete replacement
  if (isStreamComplete) {
    setArtifact((prev) => {
      return {
        ...prev,
        contents: prev.contents.map((content) => {
          if (content.index !== newArtifactIndex) {
            return content;
          }
          return {
            index: content.index,
            content: newChunk,
          };
        }),
      };
    });
    return 0;
  }

  // Handle empty chunk
  if (newChunk === '') {
    return previouslyProcessed;
  }

  setArtifact((prev) => {
    // Calculate the remaining selected text length
    const selectedLength = selectionEnd - selectionStart;
    const remainingSelected = Math.max(0, selectedLength - previouslyProcessed);

    // Calculate the effective insertion point
    const insertionIndex = selectionStart + previouslyProcessed;

    const textToReplace =
      prev.contents.find((c) => c.index === newArtifactIndex)?.content ||
      viewingArtifactContent;

    const updatedText =
      textToReplace.slice(0, insertionIndex) + // Text before the current position
      newChunk + // New chunk
      (remainingSelected > 0 // If there's still selected text remaining
        ? textToReplace.slice(
            insertionIndex + Math.min(remainingSelected, newChunk.length),
            selectionEnd
          )
        : '') +
      textToReplace.slice(selectionEnd); // Text after the selection

    return {
      ...prev,
      contents: [
        ...prev.contents.filter((c) => c.index !== newArtifactIndex),
        {
          index: newArtifactIndex,
          content: updatedText,
        },
      ],
    };
  });

  return previouslyProcessed + newChunk.length;
}
